import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  ConfigProvider,
  Empty,
  Pagination,
  Skeleton,
  Space,
  Typography,
} from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import PageHeader from "components/PageHeader";
import { Container } from "pages/style";
import axiosApiInstance from "infrastructure/utils/api";
import Colors from "infrastructure/utils/colors";
import { NOTIFICATIONS_FEED_API, NOTIFICATIONS_MAX_BELL_ITEMS } from "../constants";
import { useNotifications } from "../context/NotificationsContext";
import NotificationEntryCard from "../components/NotificationEntryCard";
import NotificationDetailsCollapse from "../components/NotificationDetailsCollapse";
import { parseCompanyNotificationsListResponse } from "../utils/parseCompanyNotificationsListResponse";
import {
  buildBankTransferDetailRows,
  buildWalletTransactionDetailRows,
  formatNotificationPresentation,
} from "../utils/formatNotificationPresentation";
import { NotificationItem } from "../types";

const { Text } = Typography;

const mergeFirstPageItems = (
  pageItems: NotificationItem[],
  contextItems: NotificationItem[]
) => {
  const byId = new Map<string, NotificationItem>();
  contextItems.forEach((item) => byId.set(item.id, item));
  pageItems.forEach((item) => {
    if (!byId.has(item.id)) {
      byId.set(item.id, item);
    }
  });

  return [...byId.values()].sort(
    (left, right) =>
      new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime()
  );
};

const NotificationsPage = () => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir() === "rtl" ? "rtl" : "ltr";
  const isRtl = direction === "rtl";

  const {
    notifications,
    unreadCount,
    markAllAsRead,
    markAsRead,
    refreshNotifications,
  } = useNotifications();

  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(NOTIFICATIONS_MAX_BELL_ITEMS);
  const [pageItems, setPageItems] = useState<NotificationItem[]>([]);

  const fetchPage = useCallback(async (targetPage: number) => {
    setIsLoading(true);
    try {
      const response = await axiosApiInstance.get(NOTIFICATIONS_FEED_API, {
        params: { page: targetPage, per_page: pageSize },
      });
      const parsed = parseCompanyNotificationsListResponse(response.data);
      setPageItems(parsed.items);
      setTotal(parsed.total);
      setPageSize(parsed.perPage);
    } finally {
      setIsLoading(false);
    }
  }, [pageSize]);

  const pageBootstrapRef = useRef(false);
  const refreshRef = useRef(refreshNotifications);
  const markAllRef = useRef(markAllAsRead);
  refreshRef.current = refreshNotifications;
  markAllRef.current = markAllAsRead;

  useEffect(() => {
    void (async () => {
      if (!pageBootstrapRef.current) {
        pageBootstrapRef.current = true;
        await refreshRef.current();
        await markAllRef.current();
      }
      await fetchPage(page);
    })();
  }, [page, fetchPage]);

  const visibleItems = useMemo(() => {
    if (page !== 1) {
      return pageItems;
    }
    return mergeFirstPageItems(pageItems, notifications);
  }, [notifications, page, pageItems]);

  return (
    <ConfigProvider direction={direction}>
      <>
        <PageHeader title={t("notifications")} />
        <Container>
          <div
            dir={direction}
            style={{
              width: "100%",
              maxWidth: 720,
              padding: "24px 20px 32px",
              textAlign: isRtl ? "right" : "left",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                flexDirection: isRtl ? "row-reverse" : "row",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 16,
                marginBottom: 24,
                paddingBottom: 20,
                borderBottom: `1px solid ${Colors.border}`,
              }}
            >
              <Space direction="vertical" size={4} style={{ textAlign: isRtl ? "right" : "left" }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {t("notifications_unread_count", { count: unreadCount })}
                </Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {t("notifications_total_count", { count: total })}
                </Text>
              </Space>
              <Button
                type="default"
                icon={<CheckCircleOutlined />}
                onClick={() => void markAllAsRead()}
                style={{
                  borderRadius: 10,
                  height: 40,
                  paddingInline: 18,
                  borderColor: Colors.cool_green,
                  color: Colors.dark_green,
                }}
              >
                {t("notifications_mark_all_as_read")}
              </Button>
            </div>

            {isLoading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : visibleItems.length === 0 ? (
              <Empty style={{ marginTop: 48 }} description={t("notifications_empty")} />
            ) : (
              visibleItems.map((item) => {
                const presentation = formatNotificationPresentation(item, t);
                const handleSummaryActivate = async () => {
                  if (!item.isRead) {
                    await markAsRead(item.id);
                  }
                  await fetchPage(page);
                };

                const extraDetailsFooter =
                  presentation.hasBankTransferDetails && presentation.bankTransferDetails ? (
                    <NotificationDetailsCollapse
                      panelKey={`${item.id}-bank-transfer`}
                      isRtl={isRtl}
                      headerLabel={t("notifications_show_details")}
                      rows={buildBankTransferDetailRows(presentation.bankTransferDetails, t)}
                    />
                  ) : presentation.hasTransactionDetails && presentation.transactionDetails ? (
                    <NotificationDetailsCollapse
                      panelKey={`${item.id}-wallet-transaction`}
                      isRtl={isRtl}
                      headerLabel={t("notifications_show_details")}
                      rows={buildWalletTransactionDetailRows(
                        presentation.transactionDetails,
                        t
                      )}
                    />
                  ) : undefined;

                return (
                  <NotificationEntryCard
                    key={item.id}
                    eventName={item.eventName}
                    payload={item.payload}
                    title={presentation.title || ""}
                    body={presentation.body || ""}
                    relativeTime={presentation.relativeTime}
                    isRead={item.isRead}
                    isRtl={isRtl}
                    onActivate={() => void handleSummaryActivate()}
                    footer={extraDetailsFooter}
                  />
                );
              })
            )}

            {!isLoading && visibleItems.length > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: isRtl ? "flex-start" : "flex-end",
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: `1px solid ${Colors.border}`,
                }}
              >
                <Pagination
                  current={page}
                  total={total}
                  pageSize={pageSize}
                  onChange={(nextPage) => setPage(nextPage)}
                  showSizeChanger={false}
                />
              </div>
            )}
          </div>
        </Container>
      </>
    </ConfigProvider>
  );
};

export default NotificationsPage;
